import * as yup from 'yup';
import { Image } from 'react-native-image-crop-picker';
import { FORM_ERROR_MESSAGES } from '@fintronners/react-api/src/utils/formUtils/schemas/constants';
import i18n from '@fintronners/react-language/src/Lang';
import {
  REG_PHONE_NUMBER_WITH_MASK,
  REG_EMAIL,
  REG_LETTER_NUMBER,
  REG_UPPERCASE_LETTERS,
  REG_LOWERCASE_LETTERS,
  REG_NUMBERS,
  REG_SPECIAL_CHARACTERS,
  REG_FIRST_LAST_NAME,
  REG_ASCEND_STRING,
  REG_POST_BOX,
  REG_POSTAL_CODE,
  REG_US_STATE,
  REG_SSN,
  REG_CPF,
} from '@fintronners/react-utils/src/regexUtils';
import {
  AvailableAlpha3Countries,
  AvailableCountries,
} from '@fintronners/react-utils/src/countries';
import { getPhoneNumberDetails } from '@fintronners/react-utils/src/phoneNumberUtils';
import { ACCEPTED_IMAGE_FILE_MIMES } from '@fintronners/react-utils/src/fileUtils';
import { isBradescoTenant } from '@fintronners/react-utils/src/tenantUtils';
import { YES_OR_NO } from './types';

export function genericStringWhen<n extends string | string[], v>(
  name: n,
  isValue: v,
  requiredMessage: string,
) {
  return yup.string().when(name, {
    is: isValue,
    then: (schema) => schema.required(requiredMessage),
  });
}

export const getPhoneNumberSchema = (withMask: boolean = false, withAdmin: boolean = false) => {
  if (withAdmin)
    return yup
      .string()
      .required(FORM_ERROR_MESSAGES.required)
      .min(12, FORM_ERROR_MESSAGES.invalidPhoneNumberAdmin)
      .max(12, FORM_ERROR_MESSAGES.invalidPhoneNumberAdmin);
  return withMask
    ? yup
        .string()
        .required(FORM_ERROR_MESSAGES.required)
        .test('phone-format', FORM_ERROR_MESSAGES.invalidPhoneNumber, (value) => {
          if (!value) return true;
          return REG_PHONE_NUMBER_WITH_MASK.test(value);
        })
    : yup.string().required(FORM_ERROR_MESSAGES.required).length(10);
};

export const getEmailSchema = () => {
  return yup
    .string()
    .required(FORM_ERROR_MESSAGES.required)
    .test('email-format', FORM_ERROR_MESSAGES.invalidEmail, (value) => {
      if (!value) return true;
      return REG_EMAIL.test(value);
    });
};

export const getDateOfBirthSchema = () => {
  return yup.string().required(FORM_ERROR_MESSAGES.required);
};

export const getUserNameSchema = () => {
  return yup
    .string()
    .required(FORM_ERROR_MESSAGES.required)
    .min(3, FORM_ERROR_MESSAGES.minLength(3))
    .test(
      'username-format',
      FORM_ERROR_MESSAGES.invalidName(i18n.t('onboardingUsernamePassword.username')),
      (value) => {
        if (!value) return true;
        return REG_LETTER_NUMBER.test(value);
      },
    );
};

export const getPasswordSchema = () => {
  return yup
    .string()
    .required(FORM_ERROR_MESSAGES.required)
    .min(8, FORM_ERROR_MESSAGES.minLength(8))
    .matches(REG_UPPERCASE_LETTERS, FORM_ERROR_MESSAGES.invalidPasswordUppercase)
    .matches(REG_LOWERCASE_LETTERS, FORM_ERROR_MESSAGES.invalidPasswordLowercase)
    .matches(REG_NUMBERS, FORM_ERROR_MESSAGES.invalidPasswordNumber)
    .matches(REG_SPECIAL_CHARACTERS, FORM_ERROR_MESSAGES.invalidPasswordSpecialCharacter)
    .strict()
    .test(
      'no-leading-trailing-space',
      FORM_ERROR_MESSAGES.invalidPasswordLeadingTrailingSpace,
      (value) => {
        if (value) {
          return value.trim() === value;
        }
        return true;
      },
    );
};

export const getConfirmPasswordSchema = (passwordFieldName: string = 'password') => {
  return yup
    .string()
    .required(FORM_ERROR_MESSAGES.required)
    .oneOf([yup.ref(passwordFieldName)], FORM_ERROR_MESSAGES.passwordDoNotMatch);
};

export const getSixDigitsCodeSchema = () => {
  return yup
    .string()
    .required(FORM_ERROR_MESSAGES.required)
    .length(6, FORM_ERROR_MESSAGES.invalidCode);
};

export const getBooleanRequiredSchema = () => {
  return yup.bool().oneOf([true], FORM_ERROR_MESSAGES.required);
};

export const getBooleanSchema = () => {
  return yup.boolean();
};

export const getStringRequiredSchema = () => {
  return yup.string().required(FORM_ERROR_MESSAGES.required);
};

export const getStringOptionalSchema = () => {
  return yup.string().optional();
};

export const getBooleanWhenSchema = (name: string) => {
  return yup.boolean().when('$context', (context: Record<string, any> | undefined, schema) => {
    return context?.[name] ? getBooleanRequiredSchema() : schema.notRequired();
  });
};

export const getArrayNumbersSchema = () => {
  return yup.array().of(yup.number().required()).min(1).required(FORM_ERROR_MESSAGES.required);
};

export const getArrayOfSchema = <T>(schema: yup.Schema<T>) => {
  return yup.array().of(schema).required(FORM_ERROR_MESSAGES.required);
};

export const getAscendStringRequiredSchema = (field: string) => {
  return yup
    .string()
    .required(FORM_ERROR_MESSAGES.required)
    .test('ascend-string-format', FORM_ERROR_MESSAGES.invalidInputAscend(field), (value) => {
      if (!value) return true;
      return REG_ASCEND_STRING.test(value);
    });
};

export const getOneOfEnumSchema = <T extends string>(enumValues: Record<string, T>) => {
  return yup.string().oneOf(Object.values(enumValues)).required(FORM_ERROR_MESSAGES.required);
};

export const getFirstLastNameSchema = (field: 'First name' | 'Last name') => {
  return yup
    .string()
    .required(FORM_ERROR_MESSAGES.required)
    .trim()
    .max(20, FORM_ERROR_MESSAGES.maxLength(20))
    .test('first-last-name-format', FORM_ERROR_MESSAGES.invalidName(field), (value) => {
      if (!value) return true;
      return REG_FIRST_LAST_NAME.test(value);
    });
};

export const getCountrySchema = () => {
  return yup.object().shape({
    name: getStringRequiredSchema(),
    flag: getStringRequiredSchema(),
    alpha2Code: getOneOfEnumSchema(AvailableCountries),
    alpha3Code: getOneOfEnumSchema(AvailableAlpha3Countries).optional(),
    countryCode: getStringRequiredSchema(),
    areaCodes: getArrayNumbersSchema(),
  });
};

export const getNewPhoneNumberSchema = (desiredCountry?: AvailableCountries) => {
  return yup
    .string()
    .required(i18n.t('errors.required'))
    .test('phone-validation', i18n.t('errors.phoneNumberMismatch'), (value, context) => {
      if (!value) {
        return context.createError({
          message: i18n.t('errors.required'),
        });
      }

      let formattedValue = value.includes('+') ? value : `+${value}`;
      const { isValid, country, nationalNumber } = getPhoneNumberDetails(formattedValue);
      if (!isValid || !country) {
        return context.createError({
          message: i18n.t('errors.invalidPhoneNumber'),
        });
      }

      if (country?.alpha2Code === AvailableCountries.BR) {
        const onlyDigits = nationalNumber?.replace(/\D/g, '') || '';

        const isCorrectLength = onlyDigits.length === 11;

        const isMobilePhoneNumber = onlyDigits.length > 2 && onlyDigits[2] === '9';

        if (!isMobilePhoneNumber || !isCorrectLength) {
          return context.createError({
            message: i18n.t('errors.invalidPhoneNumber'),
          });
        }

        const areaCode = parseInt(onlyDigits.slice(0, 2), 10);

        if (!country.areaCodes.includes(areaCode)) {
          return context.createError({
            message: i18n.t('errors.invalidPhoneNumber'),
          });
        }
      }

      if (country?.alpha2Code === AvailableCountries.US) {
        const areaCode = parseInt(nationalNumber?.toString().slice(0, 3) || '0', 10);
        if (!country.areaCodes.includes(areaCode)) {
          return context.createError({
            message: i18n.t('errors.invalidPhoneNumber'),
          });
        }
      }

      if (desiredCountry && country.alpha2Code !== desiredCountry) {
        return context.createError({
          message: i18n.t('errors.invalidPhoneNumber'),
        });
      }

      return true;
    });
};

export const getImageFileSchema = (field: string, is?: any) => {
  return yup.mixed().when(field, (fieldValues, schema) => {
    if (is !== undefined && fieldValues[0] === is) {
      return schema
        .required(i18n.t('errors.required'))
        .test('fileType', i18n.t('errors.imageFileRequired'), (value) => {
          return value instanceof File && ACCEPTED_IMAGE_FILE_MIMES.includes(value.type);
        });
    }

    return schema.notRequired();
  });
};

export const getYesOrNoSchema = () => {
  return yup
    .mixed<YES_OR_NO>()
    .oneOf(Object.values(YES_OR_NO))
    .required(FORM_ERROR_MESSAGES.required);
};

export const getAlphaNumericSchema = (key: string) => {
  return yup
    .string()
    .required(FORM_ERROR_MESSAGES.required)
    .test('alpha-numeric-format', FORM_ERROR_MESSAGES.invalidName(key), (value) => {
      if (!value) return true;
      return REG_LETTER_NUMBER.test(value);
    });
};

/**
 * @description Schema for document upload (React Native Image Crop Picker)
 * @returns yup.mixed<Partial<Image>>
 */
export const getDocumentUploadSchema = () => {
  return yup.mixed<Partial<Image>>().optional();
};

export const getStringMinMaxSchema = (min: number, max: number) => {
  return getStringRequiredSchema()
    .min(min, FORM_ERROR_MESSAGES.minLength(min))
    .max(max, FORM_ERROR_MESSAGES.maxLength(max));
};

export const getStreetAddressSchema = () => {
  return getStringMinMaxSchema(3, 60)
    .test('no-po-box', FORM_ERROR_MESSAGES.invalidAddress, (value) => {
      if (!value) return true;
      return !REG_POST_BOX.test(value);
    })
    .trim()
    .test('no-leading-space', FORM_ERROR_MESSAGES.cannotStartWithSpace, (value) => {
      if (!value) return true;
      return !value.startsWith(' ');
    });
};

export const getCitySchema = () => {
  return getStringMinMaxSchema(3, 30)
    .trim()
    .test('no-leading-space', FORM_ERROR_MESSAGES.cannotStartWithSpace, (value) => {
      if (!value) return true;
      return !value.startsWith(' ');
    });
};

export const getStateSchema = (_country?: string) => {
  return getStringRequiredSchema().when('country', {
    is: 'US',
    then: (schema) =>
      schema.matches(REG_US_STATE, FORM_ERROR_MESSAGES.invalidStateCode).uppercase(),
    otherwise: (schema) =>
      schema.min(2, FORM_ERROR_MESSAGES.minLength(2)).max(30, FORM_ERROR_MESSAGES.maxLength(30)),
  });
};

export const getPostalCodeSchema = (_country?: string) => {
  return getStringRequiredSchema().when('country', {
    is: 'US',
    then: (schema) =>
      schema
        .matches(REG_POSTAL_CODE, FORM_ERROR_MESSAGES.invalidPostalCode)
        .length(5, FORM_ERROR_MESSAGES.postalCodeExactLength),
    otherwise: (schema) =>
      schema.min(3, FORM_ERROR_MESSAGES.minLength(3)).max(10, FORM_ERROR_MESSAGES.maxLength(10)),
  });
};

export const getSSNSchema = () => {
  return yup
    .string()
    .required(FORM_ERROR_MESSAGES.required)
    .test('ssn-format', FORM_ERROR_MESSAGES.invalidSSN, function (value) {
      if (!value || value.trim() === '') {
        return true;
      }

      if (value.length !== 11) {
        // we are including the - as part of the length
        return this.createError({ message: FORM_ERROR_MESSAGES.invalidSSNLength });
      }

      return REG_SSN.test(value);
    });
};

export const getCPFSchema = () => {
  return yup
    .string()
    .required(FORM_ERROR_MESSAGES.required)
    .test('cpf-format', FORM_ERROR_MESSAGES.invalidCPF, function (value) {
      if (!value || value.trim() === '') {
        return true;
      }

      let formattedValue = value;

      formattedValue = value.replace(/\D/g, '');
      if (formattedValue.length > 11) {
        formattedValue = formattedValue.slice(0, 11);
      }
      if (formattedValue.length === 11) {
        formattedValue = formattedValue.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
      }

      if (formattedValue.length !== 14) {
        return this.createError({ message: FORM_ERROR_MESSAGES.invalidCPFLength });
      }

      return REG_CPF.test(formattedValue);
    });
};

export const getTaxIdSchema = () => {
  const isBradesco = isBradescoTenant();
  return isBradesco ? getCPFSchema() : getSSNSchema();
};
